import { I18nThemed } from 'interfaces/mixins';
import { lccstore } from 'lcc-state';
import timesync from 'timesync';

import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import has from '@dojo/framework/core/has';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { Messages } from '@dojo/framework/i18n/i18n';
import Button from '@dojo/widgets/button';
// import { formatAriaProperties } from '@dojo/widgets/common/util';
// import Label from '@dojo/widgets/label';
import TextInput from '@dojo/widgets/text-input';

import css from '../css/mcquestion.m.css';
import neCss from '../css/numeric-entry.m.css';
import { IResult, QFResultData, QRLocator, Question, QuestionTypeProperties } from '../interfaces';
import { setQuestion } from '../support/state';
import { Correct } from '../widgets/correct';

export interface NumericEntryProperties extends QuestionTypeProperties {
	question: Question;
	quizkind: string;
	localizedBundle: { isPlaceholder: boolean; format: any | null; messages: Messages };
}

const ISSEEN = 0;
const ISCOMPLETE = 1;
const ISCORRECT = 2;
const HINTBACK = 3;

@theme(css)
export class NumericEntryBase extends I18nThemed<NumericEntryProperties> {
	private _propsInited: boolean = false;

	// Added this new private function to modify the content of the question for A11Y situations
	protected modifyQuestion(htmlText: string): string {
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = htmlText;
		const strongTag = tempDiv.querySelector('strong');
		if (strongTag) {
			const parentNode = strongTag.parentNode;
			const h2Tag = document.createElement('h2');
			h2Tag.appendChild(strongTag);
			parentNode?.insertBefore(h2Tag, parentNode.firstChild);
		}
		const divElements = tempDiv.querySelectorAll('div');
		const lastDiv = divElements[divElements.length - 1];
		if (lastDiv) {
			lastDiv.setAttribute('id', 'question-label');
		}
		const modifiedText = tempDiv.innerHTML;
		return modifiedText;
	}

	protected onChange(value: string): void {
		const { question, quizstore } = this.properties;
		question.answer = value;
		setQuestion(quizstore)({ question });
		this.invalidate();
	}

	protected onAttach(): void {
		lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
	}

	@beforeProperties()
	ensure_nls(properties: NumericEntryProperties & WidgetProperties): NumericEntryProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls.get('quiz');
		if (!bundle) {
			properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} as Messages };
		} else {
			properties.localizedBundle = this.localizeBundle(bundle);
		}
		return properties;
	}

	protected update_result(): Promise<any> {
		const { question, updater } = this.properties;
		const { icode, status, cursor, answer } = question;

		question.flags[ISSEEN] = true;
		question.flags[ISCOMPLETE] = !!answer;

		const data: QFResultData = {
			answer,
			icode: { ...icode, _answer: answer as any },
			starttime: question.starttime!,
			stoptime: timesync.now().toJSON(),
			isseen: true,
			ismarked: question.marked ? true : false,
			iscorrect: question.flags[ISCORRECT]
		};
		const qrlocator: QRLocator = {
			status,
			cursor,
			qrid: '',
			quiz: ''
		};
		const result: IResult = { qrlocator, data };
		return updater([result]);
	}

	protected validateAnswer(): boolean {
		const { question } = this.properties;
		const { icode = {}, answer, ans, tol } = question;
		const _raw = ans && ans in icode ? icode[ans].raw : undefined;
		const _correct = _raw ? Number(_raw) : undefined;
		const _answer = answer ? Number(answer) : undefined;
		const _tol = tol ? Number(tol) : 0;

		// return false if something isn't defined
		if (_answer === undefined || _correct === undefined) return false;

		const ceiling = _correct + _tol;
		const floor = _correct - _tol;
		return _answer >= floor && _answer <= ceiling;
	}

	protected set_iscorrect(): void {
		const { question } = this.properties;
		question.flags[ISCORRECT] = this.validateAnswer();
	}

	protected submitted(): void {
		this.update_result();
		const { question, quizstore } = this.properties;
		question.status = 'feedback';
		setQuestion(quizstore)({ question });
		this.invalidate();
	}

	protected get_numberofquestions(): number {
		const { quizstore } = this.properties;
		const { get, path } = quizstore;
		return get(path('info')).numberofquestions;
	}

	protected render_hint(): string {
		const { question, localizedBundle } = this.properties;
		const { ans, icode = {} } = question;
		const { hint = '[quiz (numeric entry)] hint' } = localizedBundle.messages;
		const _raw = ans && ans in icode ? icode[ans].raw : undefined;
		const showflag = has('use-hints') && _raw;
		return showflag ? `${hint}: ${_raw}` : '';
	}

	protected check_hintback(): void {
		const { question } = this.properties;
		const { hintback, hintbackstatus, answer } = question;
		if (hintbackstatus === 'initial') {
			const incorrect = !question.flags[ISCORRECT] || false;
			question.flags[HINTBACK] = (Boolean(!answer) || incorrect) && hintback !== '<p>\n</p>';
			question.hintbackstatus = 'checked';
		}
	}

	protected render_hintback(): DNode {
		const { question, localizedBundle } = this.properties;
		const { hintback } = question;
		const { hint = '[quiz (numeric entry)] hint' } = localizedBundle.messages;
		question.hintbackstatus = 'rendered';
		return v('div', { key: 'hintback', classes: [neCss.hintback], xqa: 'hintback' }, [
			`${hint}:`,
			v('div', { innerHTML: hintback })
		]);
	}

	protected render_buttons(which: { islast?: boolean; isfeedback?: boolean }): DNode[] {
		const {
			messages: { finish: finishText = '', next_question: nextText = '', submit: submitText = '' }
		} = this.properties.localizedBundle;
		const { onNextQuestion, onFinish, theme, question } = this.properties;
		const { valid } = question;
		const { islast = false, isfeedback = false } = which;
		let inFinish = false;
		const disabled = valid ? !valid?.valid : false;

		const buttons = isfeedback
			? // buttons on feedback view
			  islast
				? w(
						Button,
						{
							theme,
							key: 'finish',
							onClick: async (): Promise<void> => {
								if (!inFinish) {
									inFinish = true;
									this.set_iscorrect();
									await this.update_result();
									onFinish();
								}
							}
						},
						[finishText]
						// tslint:disable-next-line:indent
				  )
				: w(
						Button,
						{
							theme,
							key: 'next',
							onClick: (): void => {
								this.update_result();
								onNextQuestion();
							}
						},
						[nextText]
						// tslint:disable-next-line:indent
				  )
			: // buttons on question view
			  w(
					Button,
					{
						theme,
						disabled,
						key: 'submit',
						onClick: (): void => {
							this.set_iscorrect();
							this.check_hintback();
							if (question.hintbackstatus === 'checked' && question.flags[HINTBACK]) {
								question.hintbackstatus = 'render';
								this.invalidate();
							} else {
								this.submitted();
							}
						}
					},
					[submitText]
					// tslint:disable-next-line:indent
			  );
		return [buttons];
	}

	protected render_question(): DNode {
		const { question } = this.properties;

		const {
			messages: { answerHelp = '[quiz (numeric entry)] answerHelp' }
		} = this.properties.localizedBundle;

		const { text, id, questiontype, valid, answer } = question;
		const numberofquestions = this.get_numberofquestions();
		const islast = question.cursor + 1 === numberofquestions;

		const modifiedText = this.modifyQuestion(text);

		return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
			v('div', { key: 'question-text', classes: [this.theme(css.question)], innerHTML: modifiedText }),
			question.hintbackstatus === 'render' ? this.render_hintback() : null,
			w(TextInput, {
				// prob want to wrap this in a div
				//theme: [this.theme(css.neInput)],
				valid: valid || { valid: true },
				onValidate: (valid, message): void => {
					question.valid = { valid, message };
				},
				value: answer || '', //answer,
				placeholder: this.render_hint(), //promptText,
				pattern: '^$|^-?(\\d+)?(\\.?\\d*)?$',
				helperText: answerHelp,
				onValue: (value: string): void => this.onChange(value)
			}),
			v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
				v(
					'div',
					{ key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] },
					this.render_buttons({ islast })
				)
			])
		]);
	}

	protected render_feedback(): DNode {
		const {
			messages: {
				correct: correctText = '',
				incorrect: incorrectText = '',
				rationale: rationaleText = '',
				for_more_info: fmiText = '',
				noAnswerProvided = '[quiz (numeric entry)] noAnswerProvided'
				// distractor_legend: distractorLegend = ''
			},
			format
		} = this.properties.localizedBundle;
		const { question } = this.properties;
		const { text, feedback, refname, id, questiontype, answer } = question;
		const numberofquestions = this.get_numberofquestions();
		const islast = question.cursor + 1 === numberofquestions;
		const iscorrect = question.flags[ISCORRECT];
		const modifiedText = this.modifyQuestion(text);

		return v('div', { key: id, xqa: `${questiontype} ${id}`, classes: [this.theme(css.root)] }, [
			v('div', { key: 'feedback-block', classes: [this.theme(css.feedback)] }, [
				v('h2', { key: 'rationale', classes: [this.theme(css.rationale)] }, [rationaleText]),
				v('div', { key: 'correct', classes: [] }, [
					v('span', { key: 'you-answered' }, [
						answer ? format('you_answered', { formattedCandidateAnswer: answer }) : noAnswerProvided,
						w(Correct, { iscorrect, title: iscorrect ? correctText : incorrectText })
					])
				]),
				v('div', { innerHTML: feedback }),
				v('p', { classes: [this.theme(css.fmi)] }, [`${fmiText} ${refname}`])
			]),
			v('div', {
				key: 'question-text',
				classes: [this.theme(css.question)],
				innerHTML: modifiedText
			}),
			w(TextInput, {
				// prob want to wrap this in a div
				//theme: [this.theme(css.neInput)],
				value: answer || '', //answer,
				// placeholder: '', //promptText,
				// pattern: '(^$|^[0-9.,]+$)',
				// helperText: 'Please enter a valid number',
				disabled: true
				// onValue: (value: string) => this.onChange(value)
			}),
			v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
				v(
					'div',
					{ key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] },
					this.render_buttons({ islast, isfeedback: true })
				)
			])
		]);
	}

	protected render(): DNode {
		const { isPlaceholder } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return null;
		}
		const { question } = this.properties;
		if (!question) {
			return null;
		}
		const { status } = question;

		return status === 'question' ? this.render_question() : this.render_feedback();
	}

	@beforeProperties()
	protected stateProperties(
		properties: NumericEntryProperties & WidgetProperties
	): NumericEntryProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		if (!this._propsInited) {
			const paths = [['question'], ['quiz'], ['loaded']];
			const invalidatePaths = paths.map((path: any) => quizstore.path(path.join('/')));
			const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
			this.own({
				destroy: () => {
					handle.remove();
				}
			});
			const h2 = lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
			this.own({
				destroy: () => {
					h2.remove();
				}
			});
			this._propsInited = true;
		}

		properties.quizkind = get(path('quiz')).kind;
		properties.question = get(path('question'));
		const { hintbackstatus } = properties.question;
		properties.question.hintbackstatus = hintbackstatus === undefined ? 'initial' : hintbackstatus;
		properties.loaded = get(path('loaded'));
		return properties;
	}
}

export default NumericEntryBase;
